'use client';

import { type RefObject, useEffect } from 'react';

/**
 * Corrects scroll position when an element above the visible viewport changes height,
 * replicating native CSS scroll-anchoring for cases where the browser's own implementation
 * fails (e.g. React replacing skeleton content with real nodes).
 *
 * The condition uses `rect.bottom - delta` (the element's bottom *before* the height change)
 * because getBoundingClientRect fires after React has already committed the new DOM, so
 * `rect.bottom` reflects the post-change size and would incorrectly skip compensation for
 * large sections that were barely visible before growing.
 */
function useScrollAnchor(elRef: RefObject<HTMLElement | null>, stickyOffset: number) {
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    let prevHeight = el.offsetHeight;

    const ro = new ResizeObserver(() => {
      const newHeight = el.offsetHeight;
      const delta = newHeight - prevHeight;
      prevHeight = newHeight;

      if (delta === 0) return;

      const rect = el.getBoundingClientRect();
      const rectBottomBeforeChange = rect.bottom - delta;

      if (rect.top <= 0 && rectBottomBeforeChange <= window.innerHeight) {
        window.scrollBy({ top: delta, behavior: 'instant' });
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [elRef, stickyOffset]);
}

export { useScrollAnchor };
